import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  User,
  Languages,
} from "lucide-react";
import useLanguage from "../utils/useLanguage";
import { UserAuth } from "../utils/AuthContext";
import { DataContext } from "../utils/DataContext";
import { getSidebarLinks } from "./sidebarConfig";
import { resolveIcon } from "../utils/resolveIcon";
import ChartModal from "./ChartModal";
import UploadInvoiceHeaderAndFooterModal from "./UploadInvoiceHeaderAndFooterModal";
import AddBankModal from "../pages/admin-pages/AddBankModal";
import Tooltip from "./Tooltip";
import AddEduStageModal from "../pages/school/AddEduStageModal";

import AddKassaModal from "./AddKassaModal";

const STORAGE_KEY = "sidebar-pinned";
const SECTIONS_STORAGE_KEY = "sidebar-sections";

const Sidebar = ({ onPinnedChange, onExpandedChange }) => {
  const [pinned, setPinned] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === "true";
  });
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SECTIONS_STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  });

  const { userData } = useContext(UserAuth);
  const { fetchShippedContainers } = useContext(DataContext);
  const { language, changeLanguage, currentDirection } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("general");

  const isRTL = currentDirection === "rtl";

  // Modal states
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [showUploadDesignModal, setShowUploadDesignModal] = useState(false);
  const [showAddStageModal, setShowAddStageModal] = useState(false);
  const [showKassaModal, setShowKassaModal] = useState(false);
  const [kassaName, setKassaName] = useState("");
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState("");

  const chartLabels = {
    label_1: t("finalAmountForCustomer"),
    label_2: t("amountForSupplier"),
  };
  const chartDataSets = {
    dataSet_1: "finalAmount",
    dataSet_2: "amountForSupplier",
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleAction = (action) => {
    if (action === "diagrams") {
      const fetchChartData = async () => {
        const response = await fetchShippedContainers();
        const shippedContainers = response?.shippedContainers;
        const data = shippedContainers.map((container) => ({
          finalAmount: container.finalAmountForCustomer,
          amountForSupplier: container.amountForSupplier,
          date: container.shippingDate,
        }));
        setChartData(data);
      };
      fetchChartData();
      setChartModalOpen(true);
    } else if (action === "uploadDesign") {
      setShowUploadDesignModal(true);
    } else if (action === "addBank") {
      setShowBankModal(true);
    } else if (action === "addStage") {
      setShowAddStageModal(true);
    } else if (action === "addKassa") {
      setShowKassaModal(true);
    }
  };

  useEffect(() => {
    localStorage.setItem(
      SECTIONS_STORAGE_KEY,
      JSON.stringify(expandedSections),
    );
  }, [expandedSections]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, pinned);
    if (onPinnedChange) onPinnedChange(pinned);
  }, [pinned]);

  useEffect(() => {
    if (onPinnedChange) onPinnedChange(pinned);
  }, []);

  const [isMobile, setIsMobile] = useState(false);

  const expanded = isMobile ? mobileOpen : pinned || hovered;

  useEffect(() => {
    if (onExpandedChange) onExpandedChange(expanded);
  }, [expanded]);
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set body dir for RTL
  useEffect(() => {
    document.body.setAttribute("dir", isRTL ? "rtl" : "ltr");
  }, [isRTL]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const { organizationType, userType } = userData || {};
  const links = getSidebarLinks(organizationType, userType);

  const filteredLinks = links.filter((link) => {
    // orgOnly → يظهر فقط إذا المستخدم organization
    if (link.orgOnly && userType !== "organization") return false;

    // userOnly → يظهر فقط إذا المستخدم user
    if (link.userOnly && userType !== "orgUser") return false;

    return true;
  });

  const disabledSections = new Set(
    filteredLinks.filter((l) => l.disabledSection).map((l) => l.section),
  );

  const isActive = (route) => location.pathname === route;

  const getDashboardPath = () => {
    if (userType === "admin" || userType === "superadmin")
      return "/iraqi-banks-page";
    if (userType === "organization" || userType === "orgUser") {
      switch (organizationType) {
        case "CLINIC":
          return "/appointments";
        case "PHARMACY":
          return "/appointments";
        case "MERCHANT":
          return "/invoicesOfShippedContainers";
        case "NO_CUSTOMER_MERCHANT":
          return "/noCustomerInvoices";
        case "SUPERMARKET":
          return "/cashier";
        case "FABRICSTORE":
          return "/rulers";
        case "PAPERFACTORY":
          return "/manage-paper-rulers";
        case "SCHOOL":
          return "/students";
        case "BANK":
          return "/banks-invoices-applications";
        case "SQUAD":
          return "/calculate-attendance";
        case "AI_ORGANIZATION":
          return "/bank-invoices";
        default:
          return "/invoiceManagement";
      }
    }
    return "/invoiceManagement";
  };

  const ToggleIcon = (() => {
    if (isRTL) return pinned ? ChevronRight : ChevronLeft;
    return pinned ? ChevronLeft : ChevronRight;
  })();

  const toggleLanguage = () => {
    changeLanguage(language === "ar" ? "en" : "ar");
  };

  return (
    <>
      {/* Burger menu button — mobile only (only show when sidebar is closed) */}
      {isMobile && !mobileOpen && (
        <button
          className="burger-menu"
          onClick={() => setMobileOpen(true)}
          aria-label="Toggle menu"
        >
          <Menu size={28} />
        </button>
      )}

      {/* Overlay — mobile only */}
      {isMobile && mobileOpen && (
        <div
          className="c-sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <nav
        className={`c-sidebar  ${expanded ? "c-sidebar--expanded" : ""} ${pinned ? "c-sidebar--pinned" : ""} ${isRTL ? "c-sidebar--rtl" : ""} ${isMobile ? "c-sidebar--mobile" : ""} ${mobileOpen ? "c-sidebar--open" : ""}`}
        dir={currentDirection}
        onMouseEnter={() => !isMobile && setHovered(true)}
        onMouseLeave={() => !isMobile && setHovered(false)}
      >
        {/* Mobile close button — inside sidebar */}
        {isMobile && (
          <button
            className="c-sidebar__close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        )}

        {/* Logo — desktop only */}
        {!isMobile && (
          <Link to={getDashboardPath()} className="c-sidebar__logo">
            <img src={resolveIcon("logo")} alt="Ehsibly" />
          </Link>
        )}

        {/* Pin toggle — desktop only */}
        {!isMobile && (
          <button
            className="c-sidebar__toggle"
            onClick={() => setPinned((prev) => !prev)}
            aria-label={pinned ? "Unpin sidebar" : "Pin sidebar"}
          >
            <ToggleIcon size={18} />
          </button>
        )}

        <ul className="c-sidebar__nav u-styled-scrollbar">
          {filteredLinks.map((link, index) => {
            const Icon = link.icon;
            const active = link.route ? isActive(link.route) : false;
            const showSection =
              link.section &&
              (index === 0 ||
                link.section !== filteredLinks[index - 1]?.section);
            const isSectionDisabled = disabledSections.has(link.section);
            const isSectionCollapsed =
              isSectionDisabled ||
              (expanded ? !expandedSections[link.section] : false);
            const isLinkDisabled = link.disabled || isSectionDisabled;
            return (
              <li key={link.route || link.action} className="c-sidebar__item">
                {showSection &&
                  (() => {
                    const sectionBtn = (
                      <button
                        className={`c-sidebar__section ${expanded ? "c-sidebar__section--visible" : ""} ${isSectionDisabled ? "c-sidebar__section--disabled" : ""}`}
                        onClick={() => toggleSection(link.section)}
                        disabled={isSectionDisabled}
                      >
                        {t(link.section)}
                        <span className="c-sidebar__section-line" />
                        <ChevronDown
                          size={12}
                          className={`c-sidebar__section-chevron ${isSectionDisabled || !expandedSections[link.section] ? "c-sidebar__section-chevron--collapsed" : ""}`}
                        />
                      </button>
                    );
                    return isSectionDisabled ? (
                      <Tooltip
                        content={t("lockedForUnsabscribers")}
                        position={isRTL ? "left" : "right"}
                      >
                        {sectionBtn}
                      </Tooltip>
                    ) : (
                      sectionBtn
                    );
                  })()}
                {!isSectionCollapsed &&
                  (() => {
                    const linkBtn = (
                      <button
                        className={`c-sidebar__link ${active && !isSectionDisabled ? "c-sidebar__link--active" : isLinkDisabled ? "c-sidebar__link--disabled" : ""}`}
                        onClick={() =>
                          !isLinkDisabled &&
                          (link.action
                            ? handleAction(link.action)
                            : navigate(link.route))
                        }
                        title={!expanded ? t(link.translationKey) : undefined}
                        disabled={isLinkDisabled}
                      >
                        <Icon size={20} className="c-sidebar__icon" />
                        <span className="c-sidebar__label">
                          {t(link.translationKey)}
                        </span>
                      </button>
                    );
                    return isLinkDisabled ? (
                      <Tooltip content={t("lockedForUnsabscribers")}>
                        {linkBtn}
                      </Tooltip>
                    ) : (
                      linkBtn
                    );
                  })()}
              </li>
            );
          })}
        </ul>

        {showBankModal ? (
          <AddBankModal
            showBankModal={showBankModal}
            setShowBankModal={setShowBankModal}
          />
        ) : null}
        {/* Footer: language + profile */}
        <div className="c-sidebar__footer">
          {/* Language toggle */}
          <button
            className="c-sidebar__link"
            onClick={toggleLanguage}
            title={
              !expanded
                ? language === "ar"
                  ? "English"
                  : "العربية"
                : undefined
            }
          >
            <Languages size={20} className="c-sidebar__icon" />
            <span className="c-sidebar__label">
              {language === "ar" ? "English" : "العربية"}
            </span>
          </button>

          {/* Profile */}

            <button
              className={`c-sidebar__link ${isActive("/profile") ? "c-sidebar__link--active" : ""}`}
              onClick={() => navigate("/profile")}
              title={!expanded ? t("profile") : undefined}
            >
              <User size={20} className="c-sidebar__icon" />
              <span className="c-sidebar__label">{t("profile")}</span>
            </button>

        </div>
      </nav>

      {/* Chart Modal */}
      <ChartModal
        open={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        data={chartData}
        labels={chartLabels}
        dataSets={chartDataSets}
      />

      {/* Upload Design Modal */}
      {showUploadDesignModal && (
        <UploadInvoiceHeaderAndFooterModal
          setShowInvoiceHeaderModal={setShowUploadDesignModal}
        />
      )}

      {showAddStageModal && (
        <AddEduStageModal setShowAddStageModal={setShowAddStageModal} />
      )}

      {showKassaModal && (
        <AddKassaModal setShowKassaModal={setShowKassaModal} />
      )}
    </>
  );
};

export default Sidebar;
